import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col } from 'react-bootstrap'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { MultiSelectWithActions } from 'app/tfi/components/MultiSelectWithActions'
import { DateInput } from 'app/tfi/components/DateInput'
import { GET_ALL_POLICIES_STATUSES, GET_ALL_POLICIES_TYPES } from '../../../gql/queries/policiesQueries'
import { GET_ALL_USERS_FILTER, GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { GET_ALL_LEAD_SOURCES } from '../../../gql/queries/leadSourceQuery'
import { getTagContrastColor, parseId } from '../../../helpers'
import {
  IMMIGRATION_STATUS_CHOICES,
  DOCUMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  METAL_LEVELS,
} from '../../../helpers/catalogs'

const currentYear = new Date().getFullYear()
const EFFECTIVE_YEARS = [currentYear - 1, currentYear, currentYear + 1]

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

const CARRIER_STATUS_OPTIONS = [
  { value: 'IN_FORCE', label: 'In Force' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'GRACE_PERIOD', label: 'Grace Period' },
  { value: 'PENDING_EFFECTUATION', label: 'Pending Effectuation' },
  { value: 'PAST_DUE', label: 'Past Due' },
  { value: 'DELINQUENT', label: 'Delinquent' },
  { value: 'FUTURE_EFFECTIVE', label: 'Future Effective' },
  { value: 'UNKNOWN', label: 'Unknown' },
]

const COMMISSION_PAYMENT_STATUS_OPTIONS = [
  { value: 'NEVER_STARTED', label: 'Never Started' },
  { value: 'CANCELED', label: 'Canceled' },
  { value: 'SIN_COMISION', label: 'Sin Comisión' },
  { value: 'STAND_BY', label: 'Stand By' },
]

export interface RecipientFilters {
  // Person / assignment
  user: string[]
  assignedTo: string[]
  // Location
  state: string[]
  stateJurisdictionType: string
  zipCode: string
  // Policy basics
  carrier: string[]
  policyStatus: string[]
  policyType: string[]
  isPolicyHolder: boolean | undefined
  withoutPolicy: boolean | undefined
  unassigned: boolean | undefined
  effectiveYears: number[]
  // Personal attributes
  immigrationStatus: string[]
  documentStatus: string[]
  paymentStatus: string[]
  leadSource: string[]
  metalLevel: string
  holderGender: string
  hasMemberId: boolean | undefined
  // Commission
  commissionPayable: boolean | undefined
  commissionPaymentStatus: string
  // Property / insurance extras
  ownsCar: boolean | undefined
  ownsHome: boolean | undefined
  hasAutoPolicy: boolean | undefined
  hasHomePolicy: boolean | undefined
  // Tags & dates
  tags: string[]
  policyCreatedFrom: string
  policyCreatedTo: string
  submissionDateFrom: string
  submissionDateTo: string
  // Carrier import filters
  carrierStatusGeneric: string[]
  carrierLastAsOfDateAfter: string
  carrierLastAsOfDateBefore: string
  carrierIsAgeIn: boolean | undefined
  carrierIsDelinquent: boolean | undefined
  carrierIsUnpaidPending: boolean | undefined
  carrierIsUnpaidPastDue: boolean | undefined
}

export const EMPTY_RECIPIENT_FILTERS: RecipientFilters = {
  user: [],
  assignedTo: [],
  state: [],
  stateJurisdictionType: '',
  zipCode: '',
  carrier: [],
  policyStatus: [],
  policyType: [],
  isPolicyHolder: undefined,
  withoutPolicy: undefined,
  unassigned: undefined,
  effectiveYears: [],
  immigrationStatus: [],
  documentStatus: [],
  paymentStatus: [],
  leadSource: [],
  metalLevel: '',
  holderGender: '',
  hasMemberId: undefined,
  commissionPayable: undefined,
  commissionPaymentStatus: '',
  ownsCar: undefined,
  ownsHome: undefined,
  hasAutoPolicy: undefined,
  hasHomePolicy: undefined,
  tags: [],
  policyCreatedFrom: '',
  policyCreatedTo: '',
  submissionDateFrom: '',
  submissionDateTo: '',
  carrierStatusGeneric: [],
  carrierLastAsOfDateAfter: '',
  carrierLastAsOfDateBefore: '',
  carrierIsAgeIn: undefined,
  carrierIsDelinquent: undefined,
  carrierIsUnpaidPending: undefined,
  carrierIsUnpaidPastDue: undefined,
}

interface RecipientFilterModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: RecipientFilters) => void
  initialFilters: RecipientFilters
  states: { id: string; name: string; code: string }[]
  carriers: { id: string; name: string }[]
  tags: { id: string; name: string; color: string }[]
}

function boolVal(val: boolean | undefined): { value: string; label: string } | null {
  if (val === true) return { value: 'true', label: 'Yes' }
  if (val === false) return { value: 'false', label: 'No' }
  return null
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === 'true') return true
  if (v === 'false') return false
  return undefined
}

export const RecipientFilterModal: React.FC<RecipientFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  states,
  carriers,
  tags,
}) => {
  const [filters, setFilters] = useState<RecipientFilters>(initialFilters)

  useEffect(() => {
    if (isOpen) setFilters(initialFilters)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: policyStatusesData } = useQuery(GET_ALL_POLICIES_STATUSES, { skip: !isOpen })
  const { data: policyTypesData } = useQuery(GET_ALL_POLICIES_TYPES, {
    skip: !isOpen,
    variables: { first: 100 },
  })
  const { data: agentsData } = useQuery(GET_ALL_USERS_FILTER, {
    skip: !isOpen,
    variables: { requireNpn: true },
  })
  const { data: assignedToData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    skip: !isOpen,
    variables: { assignmentCandidates: true },
  })
  const { data: leadSourcesData } = useQuery(GET_ALL_LEAD_SOURCES, {
    skip: !isOpen,
    variables: { first: 200 },
  })

  // Policy statuses grouped by category
  const rawStatuses: { code: string; title: string; category: string }[] =
    policyStatusesData?.allPolicyStatuses?.edges?.map((e: any) => e.node) ?? []

  const grouped: Record<string, { value: string; label: string }[]> = {}
  rawStatuses.forEach((s) => {
    const cat = s.category || 'OTHER'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push({ value: s.code, label: s.title })
  })
  const policyStatusOptions = Object.keys(grouped).map((cat) => ({
    label: cat.replace(/_/g, ' '),
    options: grouped[cat],
  }))
  const allStatusFlat = policyStatusOptions.flatMap((g) => g.options)

  const policyTypeOptions: { value: string; label: string }[] =
    policyTypesData?.allPoliciesType?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.typePolicy,
    })) ?? []

  const agentsOptions: { value: string; label: string }[] =
    agentsData?.allUsers?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: `${e.node.firstName} ${e.node.lastName} - ${e.node.email}${e.node.npn ? ` (${e.node.npn})` : ''}`,
    })) ?? []

  const assignedToOptions: { value: string; label: string }[] =
    assignedToData?.allUsersList?.map((u: any) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
    })) ?? []

  const leadSourceOptions: { value: string; label: string }[] =
    leadSourcesData?.allLeadSources?.edges?.map((e: any) => ({
      value: String(parseId(e.node.id)),
      label: e.node.name,
    })) ?? []

  const stateOptions = states.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))
  const carrierOptions = carriers.map((c) => ({ value: c.id, label: c.name }))
  const tagOptions = tags.map((t) => ({ value: t.id, label: t.name, color: t.color }))
  const immigrationStatusOptions = IMMIGRATION_STATUS_CHOICES
  const documentStatusOptions = DOCUMENT_STATUS_OPTIONS
  const paymentStatusOptions = PAYMENT_STATUS_OPTIONS
  const metalLevelOptions = METAL_LEVELS

  const policyRelationshipValue =
    filters.withoutPolicy === true
      ? 'no_policy'
      : filters.isPolicyHolder === true
        ? 'holder'
        : filters.isPolicyHolder === false && filters.withoutPolicy === false
          ? 'family'
          : ''

  const set = (patch: Partial<RecipientFilters>) => setFilters((f) => ({ ...f, ...patch }))

  const handleMulti = (field: keyof RecipientFilters, sel: any) =>
    set({ [field]: sel ? sel.map((o: any) => o.value) : [] } as any)

  const handleDateChange = (name: string, value: string) =>
    setFilters((f) => ({ ...f, [name]: value }))

  const handleApply = () => {
    onApply(filters)
    onClose()
  }

  const handleClear = () => setFilters(EMPTY_RECIPIENT_FILTERS)

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex justify-content-end gap-2 mb-4'>
          <Button variant='light' className='px-3' onClick={handleClear}>
            Clear
          </Button>
          <Button variant='primary' className='px-3' onClick={handleApply}>
            <i className='bi bi-search me-1' />
            Apply Filters
          </Button>
        </div>

        <Form>
          {/* ── Agent / Assignment ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Agent</Form.Label>
              <MultiSelectWithActions
                value={agentsOptions.filter((o) => filters.user.includes(o.value))}
                options={agentsOptions}
                onChange={(sel: any) => handleMulti('user', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Assigned To</Form.Label>
              <MultiSelectWithActions
                value={assignedToOptions.filter((o) => filters.assignedTo.includes(o.value))}
                options={assignedToOptions}
                onChange={(sel: any) => handleMulti('assignedTo', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Carrier</Form.Label>
              <MultiSelectWithActions
                value={carrierOptions.filter((o) => filters.carrier.includes(String(o.value)))}
                options={carrierOptions}
                onChange={(sel: any) => handleMulti('carrier', sel)}
              />
            </Col>
            <Col>
              <Form.Label>State</Form.Label>
              <MultiSelectWithActions
                value={stateOptions.filter((o) => filters.state.includes(o.value))}
                options={stateOptions}
                onChange={(sel: any) => handleMulti('state', sel)}
              />
            </Col>
            <Col>
              <Form.Label>State Jurisdiction Type</Form.Label>
              <Select
                value={
                  filters.stateJurisdictionType === 'FEDERAL'
                    ? { value: 'FEDERAL', label: 'Federal' }
                    : filters.stateJurisdictionType === 'STATE'
                      ? { value: 'STATE', label: 'State' }
                      : null
                }
                options={[
                  { value: 'FEDERAL', label: 'Federal' },
                  { value: 'STATE', label: 'State' },
                ]}
                onChange={(sel: any) => set({ stateJurisdictionType: sel?.value ?? '' })}
                isClearable
                placeholder='Select jurisdiction...'
              />
            </Col>
            <Col>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type='number'
                value={filters.zipCode}
                onChange={(e) => set({ zipCode: e.target.value })}
              />
            </Col>
          </Row>

          {/* ── Policy ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Policy Status</Form.Label>
              <MultiSelectWithActions
                value={allStatusFlat.filter((o) => filters.policyStatus.includes(o.value))}
                options={policyStatusOptions}
                onChange={(sel: any) => handleMulti('policyStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Policy Type</Form.Label>
              <MultiSelectWithActions
                value={policyTypeOptions.filter((o) => filters.policyType.includes(String(o.value)))}
                options={policyTypeOptions}
                onChange={(sel: any) => handleMulti('policyType', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Policy Relationship</Form.Label>
              <Form.Select
                value={policyRelationshipValue}
                onChange={(e) =>
                  set({
                    isPolicyHolder:
                      e.target.value === 'holder'
                        ? true
                        : e.target.value === 'family'
                          ? false
                          : undefined,
                    withoutPolicy:
                      e.target.value === 'no_policy'
                        ? true
                        : e.target.value === 'holder' || e.target.value === 'family'
                          ? false
                          : undefined,
                  })
                }
              >
                <option value=''>All contacts</option>
                <option value='holder'>Policy holders</option>
                <option value='family'>Family members</option>
                <option value='no_policy'>No policy</option>
              </Form.Select>
            </Col>
            <Col>
              <Form.Label>Unassigned Policies</Form.Label>
              <Select
                value={
                  filters.unassigned === true
                    ? { value: 'true', label: 'Only unassigned' }
                    : filters.unassigned === false
                      ? { value: 'false', label: 'Only assigned' }
                      : null
                }
                options={[
                  { value: 'true', label: 'Only unassigned' },
                  { value: 'false', label: 'Only assigned' },
                ]}
                onChange={(sel: any) => set({ unassigned: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
            <Col>
              <Form.Label>Source</Form.Label>
              <MultiSelectWithActions
                value={leadSourceOptions.filter((o) => filters.leadSource.includes(o.value))}
                options={leadSourceOptions}
                onChange={(sel: any) => handleMulti('leadSource', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Metal Level</Form.Label>
              <Select
                value={metalLevelOptions.find((o) => o.value === filters.metalLevel) ?? null}
                options={metalLevelOptions}
                onChange={(sel: any) => set({ metalLevel: sel?.value ?? '' })}
                isClearable
                placeholder='Select Metal Level'
              />
            </Col>
          </Row>

          {/* ── Personal attributes ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Immigration Status</Form.Label>
              <MultiSelectWithActions
                value={immigrationStatusOptions.filter((o) => filters.immigrationStatus.includes(o.value))}
                options={immigrationStatusOptions}
                onChange={(sel: any) => handleMulti('immigrationStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Document Status</Form.Label>
              <MultiSelectWithActions
                value={documentStatusOptions.filter((o) => filters.documentStatus.includes(o.value))}
                options={documentStatusOptions}
                onChange={(sel: any) => handleMulti('documentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Payment Status</Form.Label>
              <MultiSelectWithActions
                value={paymentStatusOptions.filter((o) => filters.paymentStatus.includes(o.value))}
                options={paymentStatusOptions}
                onChange={(sel: any) => handleMulti('paymentStatus', sel)}
              />
            </Col>
            <Col>
              <Form.Label>Gender (Holder)</Form.Label>
              <Select
                value={GENDER_OPTIONS.find((o) => o.value === filters.holderGender) ?? null}
                options={GENDER_OPTIONS}
                onChange={(sel: any) => set({ holderGender: sel?.value ?? '' })}
                isClearable
                placeholder='Select...'
              />
            </Col>
            <Col>
              <Form.Label>Member ID Status</Form.Label>
              <Select
                value={boolVal(filters.hasMemberId)}
                options={[
                  { value: 'true', label: 'With Member ID' },
                  { value: 'false', label: 'Without Member ID' },
                ]}
                onChange={(sel: any) => set({ hasMemberId: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
            <Col>
              <Form.Label>Tags</Form.Label>
              <Select
                value={tagOptions.filter((o) => filters.tags.includes(o.value))}
                options={tagOptions}
                onChange={(sel: any) =>
                  set({ tags: sel ? sel.map((o: any) => o.value) : [] })
                }
                isMulti
                closeMenuOnSelect={false}
                placeholder='Filter by tags...'
                formatOptionLabel={(option: any) => (
                  <span className='d-flex align-items-center gap-2'>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    {option.label}
                  </span>
                )}
              />
            </Col>
          </Row>

          {/* ── Commission ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-3'>
            <Col>
              <Form.Label>Commission Report Status</Form.Label>
              <Select
                value={
                  filters.commissionPayable === true
                    ? { value: 'true', label: 'Considered for commission' }
                    : filters.commissionPayable === false
                      ? { value: 'false', label: 'Not considered for commission' }
                      : null
                }
                options={[
                  { value: 'true', label: 'Considered for commission' },
                  { value: 'false', label: 'Not considered for commission' },
                ]}
                onChange={(sel: any) => set({ commissionPayable: parseBool(sel?.value) })}
                isClearable
                placeholder='All policies'
              />
            </Col>
            <Col>
              <Form.Label>Commission Payment Status</Form.Label>
              <Select
                value={
                  COMMISSION_PAYMENT_STATUS_OPTIONS.find(
                    (o) => o.value === filters.commissionPaymentStatus
                  ) ?? null
                }
                options={COMMISSION_PAYMENT_STATUS_OPTIONS}
                onChange={(sel: any) => set({ commissionPaymentStatus: sel?.value ?? '' })}
                isClearable
                placeholder='All policies'
              />
            </Col>
          </Row>

          {/* ── Property / extras ── */}
          <Row className='mb-3 g-3 row-cols-1 row-cols-md-4'>
            <Col>
              <Form.Label>Owns car</Form.Label>
              <Select
                value={boolVal(filters.ownsCar)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ ownsCar: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Owns home</Form.Label>
              <Select
                value={boolVal(filters.ownsHome)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ ownsHome: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Has auto policy</Form.Label>
              <Select
                value={boolVal(filters.hasAutoPolicy)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ hasAutoPolicy: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col>
              <Form.Label>Has home policy</Form.Label>
              <Select
                value={boolVal(filters.hasHomePolicy)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ hasHomePolicy: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
          </Row>

          {/* ── Effective Year ── */}
          <Row className='mb-3 g-3'>
            <Col xs={12}>
              <Form.Label className='fw-semibold'>Effective Year</Form.Label>
              <div className='d-flex gap-3 mt-1'>
                {EFFECTIVE_YEARS.map((year) => (
                  <Form.Check
                    key={year}
                    inline
                    label={year}
                    checked={filters.effectiveYears.includes(year)}
                    onChange={() =>
                      setFilters((f) => ({
                        ...f,
                        effectiveYears: f.effectiveYears.includes(year)
                          ? f.effectiveYears.filter((y) => y !== year)
                          : [...f.effectiveYears, year],
                      }))
                    }
                  />
                ))}
              </div>
            </Col>
          </Row>

          <hr />

          {/* ── Date Ranges ── */}
          <h6 className='text-muted mb-3'>Date Ranges</h6>
          <Row className='g-3 row-cols-1 row-cols-md-2 row-cols-xl-4 mb-3'>
            <Col>
              <DateInput
                id='policyCreatedFrom'
                name='policyCreatedFrom'
                label='Policy Created From'
                value={filters.policyCreatedFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='policyCreatedTo'
                name='policyCreatedTo'
                label='Policy Created To'
                value={filters.policyCreatedTo}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateFrom'
                name='submissionDateFrom'
                label='Submission Date From'
                value={filters.submissionDateFrom}
                onChange={handleDateChange}
              />
            </Col>
            <Col>
              <DateInput
                id='submissionDateTo'
                name='submissionDateTo'
                label='Submission Date To'
                value={filters.submissionDateTo}
                onChange={handleDateChange}
              />
            </Col>
          </Row>

          <hr />

          {/* ── Carrier Import Filters ── */}
          <h5>Carrier Import Filters</h5>
          <Row className='mb-3 g-2 align-items-end'>
            <Col xs={12} md={6} xl={3}>
              <Form.Label>Carrier Status</Form.Label>
              <Select
                value={CARRIER_STATUS_OPTIONS.filter((o) =>
                  filters.carrierStatusGeneric.includes(o.value)
                )}
                options={CARRIER_STATUS_OPTIONS}
                onChange={(sel: any) =>
                  set({ carrierStatusGeneric: sel ? sel.map((o: any) => o.value) : [] })
                }
                isMulti
                isClearable
                placeholder='Select carrier status...'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateAfter'
                name='carrierLastAsOfDateAfter'
                label='Carrier As-of Date From'
                value={filters.carrierLastAsOfDateAfter}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <DateInput
                id='carrierLastAsOfDateBefore'
                name='carrierLastAsOfDateBefore'
                label='Carrier As-of Date To'
                value={filters.carrierLastAsOfDateBefore}
                onChange={handleDateChange}
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Age-In</Form.Label>
              <Select
                value={boolVal(filters.carrierIsAgeIn)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsAgeIn: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={1}>
              <Form.Label className='small text-nowrap'>Delinquent</Form.Label>
              <Select
                value={boolVal(filters.carrierIsDelinquent)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsDelinquent: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Pending</Form.Label>
              <Select
                value={boolVal(filters.carrierIsUnpaidPending)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsUnpaidPending: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
            <Col xs={12} md={6} xl={2}>
              <Form.Label className='small text-nowrap'>Unpaid Past Due</Form.Label>
              <Select
                value={boolVal(filters.carrierIsUnpaidPastDue)}
                options={YES_NO_OPTIONS}
                onChange={(sel: any) => set({ carrierIsUnpaidPastDue: parseBool(sel?.value) })}
                isClearable
                placeholder='Any'
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' className='px-4' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' className='px-4' onClick={handleApply}>
          <i className='bi bi-search me-1' />
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
